import { BillingHold, BillingNote } from '@assets';
import { Box, Grid, Typography, TextField } from '@atgfr8/atg-ui-tailwind';
import { Button, Notification, ToastType } from '@components';
import { closeModal } from '@store/modals';
import { FlagType, deleteFlag, updateFlag } from '@store/orders';
import { FlagViewModel } from '@typeDefinitions/flagTypes';
import { IOrderViewModel } from '@typeDefinitions/orderTypes';
import { formatDataTestId } from '@utils/dataTestIdFormatter';
import { handleToast } from '@utils/utils';
import { EDIT_BILLING_HOLD_AND_NOTE } from '@views/order/constants';
import { Component, Show, createSignal } from 'solid-js';
import { CHANGE_TRACKING_SOURCES } from '@changeTracking/utils/changeTrackingRegistry';

import BillNoteFlagComments from './BillNoteFlagComments';
import { editBillingModalClasses as cls } from './classes';

export type EditBillingHoldAndNotesModalProps = {
  modalId: string;
  item: FlagViewModel;
  index: number;
  flags?: FlagViewModel[];
  updateFlags?: (flags: FlagViewModel[] | Partial<IOrderViewModel>) => void;
};

const EditBillingHoldAndNotesModal: Component<
  EditBillingHoldAndNotesModalProps
> = (props: EditBillingHoldAndNotesModalProps) => {
  const flagId = props.item.id;
  const isBillingHold = props.item.flagType === FlagType.BillingHold;

  const [description, setDescription] = createSignal(
    props.item.description ?? '',
  );

  const [deleteLoading, setDeleteLoading] = createSignal(false);
  const [updateLoading, setUpdateLoading] = createSignal(false);

  const onClose = () => closeModal(props.modalId);
  const finalize = () => {
    closeModal(props.modalId);
    setUpdateLoading(false);
    setDeleteLoading(false);
  };

  const deleteOrCloseBillingHoldAndNote = async () => {
    try {
      setDeleteLoading(true);
      flagId &&
        (await deleteFlag(
          flagId,
          undefined,
          CHANGE_TRACKING_SOURCES.orderDetails.orderHeaderFlagDelete,
        ));
      handleToast(ToastType.Success, 'Flag Removed Succesfully');

      const newFlags = props.flags?.filter((flag) => flag.id !== flagId);
      props.updateFlags && props.updateFlags(newFlags || []);

      closeModal(EDIT_BILLING_HOLD_AND_NOTE);
    } catch (error) {
      handleToast(ToastType.Error, 'Failed to delete flag');
    } finally {
      finalize();
    }
  };

  const updateBillingHoldAndNote = async () => {
    try {
      setUpdateLoading(true);
      const payload: FlagViewModel = {
        ...props.item,
        description: description(),
      };
      const response = await updateFlag(
        payload,
        CHANGE_TRACKING_SOURCES.orderDetails.orderHeaderFlagUpdate,
      );

      const updatedFlags = [...(props.flags || [])];

      updatedFlags[props.index] = response as unknown as FlagViewModel;
      props.updateFlags && props.updateFlags(updatedFlags);
      handleToast(ToastType.Success, 'Flag Updated Succesfully');
    } catch (error) {
      handleToast(ToastType.Error, 'Failed to update flag');
    } finally {
      finalize();
    }
  };

  const renderData = {
    modalTitle: isBillingHold ? 'Edit Billing Hold' : 'Edit Billing Note',
    cancelButtonText: isBillingHold ? 'Remove Flag' : 'Close Flag',
    labelText: isBillingHold ? 'Billing Hold' : 'Billing Note',
    imgSource: isBillingHold ? BillingHold : BillingNote,
  };

  return (
    <Box style={{ 'flex-grow': 1 }}>
      <Grid container spacing={4} class="px-1 pb-2 items-center">
        <Grid item size={11}>
          <Typography class={cls.modalTitle} variant="h6" component="h2">
            {renderData.modalTitle}
          </Typography>
        </Grid>
      </Grid>
      <Grid container spacing={4}>
        <Show when={isBillingHold}>
          <Grid item size={12}>
            <Notification
              type="info"
              text="Prevent Billing from processing this load until this hold is released."
            />
          </Grid>
        </Show>
        <Grid item size={{ xs: 12, sm: 10 }}>
          <Grid container spacing={5}>
            <Grid item size={12}>
              <Typography
                variant="body1"
                component="p"
                class={cls.subLabelText}
              >
                Queue
              </Typography>
              <Typography variant="body1" component="p">
                {renderData.labelText}
              </Typography>
            </Grid>
            <Grid item size={12}>
              <TextField
                multiline
                label="Description"
                placeholder="Enter description here"
                rows={2}
                value={description()}
                onChange={(value: string) => setDescription(value)}
                dataTestId="description-textInput"
              />
            </Grid>
          </Grid>
        </Grid>
        <Grid
          item
          size={{ xs: 12, sm: 2 }}
          class="flex items-center justify-center"
        >
          <img src={renderData.imgSource} class="h-20" alt="Billing Note" />
        </Grid>
        <Show when={!isBillingHold && flagId}>
          {flagId && <BillNoteFlagComments flagId={flagId} />}
        </Show>
      </Grid>
      <Grid container class="justify-end gap-2 pt-4">
        <Button
          variant="contained"
          label="Update Flag"
          onClick={updateBillingHoldAndNote}
          disabled={updateLoading() || deleteLoading()}
          isLoading={updateLoading()}
          class={cls.footerBtns}
          dataTestId={formatDataTestId('Update Flag', 'button')}
        />
        <Button
          variant="contained"
          class={`${cls.footerBtns} bg-[#d32f2f]!`}
          label={renderData.cancelButtonText}
          disabled={deleteLoading() || updateLoading()}
          isLoading={deleteLoading()}
          onClick={deleteOrCloseBillingHoldAndNote}
          dataTestId={formatDataTestId(renderData.cancelButtonText, 'button')}
        />
        <Button
          variant="text"
          label="Cancel"
          onClick={onClose}
          class={cls.footerBtns}
          dataTestId={formatDataTestId('Cancel', 'button')}
        />
      </Grid>
    </Box>
  );
};

export default EditBillingHoldAndNotesModal;
